import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Package, Search, Filter, Plus, TrendingUp, Save, Loader2, X, CheckCircle2,
  AlertTriangle, Eye, EyeOff, Trash2, Settings, Percent, RefreshCw,
} from "lucide-react";

/**
 * Rivenditore: Eredità catalogo Casa Madre.
 * Tab 1: Catalogo CM (importa) — Tab 2: I miei ereditati (gestisci markup/attivo)
 */
export default function RivenditoreInherit() {
  const [tab, setTab] = useState("catalog"); // catalog | mine
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [inherited, setInherited] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [bulkMarkup, setBulkMarkup] = useState("");

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/rivenditore/inherit-settings");
      setSettings(r.data);
    } catch (e) {
      toast.error("Errore settings: " + (e.response?.data?.detail || e.message));
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (categoriaFilter) params.set("categoria", categoriaFilter);
      const r = await api.get(`/rivenditore/casa-madre-catalog?${params.toString()}`);
      setCatalog(r.data.products || []);
    } catch (e) {
      toast.error("Errore catalogo: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [q, categoriaFilter]);

  const loadInherited = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/rivenditore/inherited");
      setInherited(r.data.items || []);
      setPendingCount(r.data.pending_price_changes || 0);
    } catch (e) {
      toast.error("Errore ereditati: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (tab === "catalog") loadCatalog();
    else loadInherited();
    setSelectedSkus(new Set());
  }, [tab, q, categoriaFilter, loadCatalog, loadInherited]);

  const categorieUniche = useMemo(() => {
    const src = tab === "catalog" ? catalog : inherited;
    return Array.from(new Set(src.map((p) => p.categoria).filter(Boolean))).sort();
  }, [catalog, inherited, tab]);

  const toggleSku = (sku) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const toggleAll = (list, keyFn = (p) => p.sku_madre) => {
    const allIds = list.map(keyFn);
    const allSel = allIds.every((id) => selectedSkus.has(id));
    setSelectedSkus(allSel ? new Set() : new Set(allIds));
  };

  // ─── Azioni Catalog (import) ───
  const bulkImport = async () => {
    if (selectedSkus.size === 0) {
      toast.warning("Seleziona almeno un prodotto");
      return;
    }
    const body = { skus: Array.from(selectedSkus) };
    if (bulkMarkup !== "" && !isNaN(parseFloat(bulkMarkup))) {
      body.markup_pct_override = parseFloat(bulkMarkup);
    }
    try {
      const r = await api.post("/rivenditore/inherit", body);
      toast.success(`Importati: ${r.data.inserted} nuovi · ${r.data.updated} aggiornati`);
      setSelectedSkus(new Set());
      setBulkMarkup("");
      loadCatalog();
    } catch (e) {
      toast.error("Errore import: " + (e.response?.data?.detail || e.message));
    }
  };

  // ─── Azioni Ereditati ───
  const bulkAction = async (action) => {
    if (selectedSkus.size === 0) {
      toast.warning("Seleziona almeno un item");
      return;
    }
    const labels = { activate: "attivati", deactivate: "disattivati", delete: "eliminati", "accept-price": "aggiornati" };
    if (action === "delete" && !window.confirm(`Eliminare ${selectedSkus.size} item ereditati?`)) return;
    try {
      const r = await api.post("/rivenditore/inherit/bulk-action", { skus: Array.from(selectedSkus), action });
      toast.success(`${r.data.affected} item ${labels[action]}`);
      setSelectedSkus(new Set());
      loadInherited();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const updateSingleMarkup = async (sku, value) => {
    const val = value === "" || value === null ? null : parseFloat(value);
    if (val !== null && isNaN(val)) return;
    try {
      await api.put(`/rivenditore/inherit/${sku}`, { markup_pct_override: val });
      toast.success(val === null ? "Override rimosso" : `Markup ${val}% impostato`);
      loadInherited();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const toggleAttivo = async (sku, current) => {
    try {
      await api.put(`/rivenditore/inherit/${sku}`, { attivo: !current });
      loadInherited();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const acceptPrice = async (sku) => {
    try {
      await api.post(`/rivenditore/inherit/${sku}/accept-price`);
      toast.success("Nuovo prezzo accettato");
      loadInherited();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const removeItem = async (sku) => {
    if (!window.confirm(`Rimuovere ${sku} dalla vetrina?`)) return;
    try {
      await api.delete(`/rivenditore/inherit/${sku}`);
      toast.success("Rimosso");
      loadInherited();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <AdminLayout title="Eredità catalogo Casa Madre">
      <div className="max-w-7xl mx-auto p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline text-brand mb-2 inline-flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Eredità Rivenditore
            </div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Catalogo Casa Madre</h1>
            <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
              Importa i prodotti della tua Casa Madre nella tua vetrina. Applica un markup% globale, per categoria o singolo. Foto e descrizioni restano allineate alla Casa Madre.
            </p>
          </div>
          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="inherit-settings-btn"
            className="h-10 px-4 rounded-sm border border-border text-sm font-medium hover:bg-slate-50 inline-flex items-center gap-2"
          >
            <Settings className="w-4 h-4" /> Impostazioni markup
          </button>
        </div>

        {/* Settings summary */}
        {settings && (
          <div className="bg-brand/5 border border-brand/20 rounded-sm p-3 mb-6 flex items-center gap-4 flex-wrap text-xs" data-testid="inherit-settings-summary">
            <Percent className="w-4 h-4 text-brand" />
            <div>
              <strong>Markup globale:</strong> +{settings.markup_default}%
            </div>
            {Object.keys(settings.markup_by_category || {}).length > 0 && (
              <div>
                <strong>Per categoria:</strong>{" "}
                {Object.entries(settings.markup_by_category).map(([k, v]) => (
                  <span key={k} className="ml-1 px-1.5 py-0.5 bg-white rounded-sm border border-slate-200">
                    {k} +{v}%
                  </span>
                ))}
              </div>
            )}
            <div className="text-muted-foreground italic ml-auto">
              Priorità: override singolo → categoria → globale
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 border-b border-border">
          <TabBtn active={tab === "catalog"} onClick={() => setTab("catalog")} testId="tab-catalog">
            <Package className="w-3.5 h-3.5" /> Catalogo Casa Madre ({catalog.length})
          </TabBtn>
          <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testId="tab-mine">
            <CheckCircle2 className="w-3.5 h-3.5" /> I miei ereditati ({inherited.length})
            {pendingCount > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 bg-amber-100 text-amber-900 rounded-sm px-1.5 py-0.5 text-[10px] font-bold">
                <AlertTriangle className="w-2.5 h-2.5" /> {pendingCount} prezzi cambiati
              </span>
            )}
          </TabBtn>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per SKU, nome, materiali…"
              data-testid="inherit-search"
              className="pl-9 h-10 rounded-sm"
            />
          </div>
          <select
            value={categoriaFilter}
            onChange={(e) => setCategoriaFilter(e.target.value)}
            data-testid="inherit-cat-filter"
            className="h-10 px-3 rounded-sm border border-border text-sm"
          >
            <option value="">Tutte le categorie</option>
            {categorieUniche.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Bulk actions bar */}
        {selectedSkus.size > 0 && (
          <div className="sticky top-0 z-10 bg-foreground text-white rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap shadow-lg" data-testid="inherit-bulk-bar">
            <span className="text-sm font-semibold">{selectedSkus.size} selezionati</span>
            {tab === "catalog" ? (
              <>
                <Input
                  value={bulkMarkup}
                  onChange={(e) => setBulkMarkup(e.target.value)}
                  placeholder={`Markup override (opz., def=${settings?.markup_default || 30}%)`}
                  data-testid="inherit-bulk-markup"
                  className="h-8 w-56 text-xs bg-white/10 border-white/20 text-white placeholder-white/50"
                />
                <button
                  onClick={bulkImport}
                  data-testid="inherit-bulk-import"
                  className="h-9 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Importa {selectedSkus.size}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => bulkAction("activate")} data-testid="inherit-bulk-activate"
                  className="h-9 px-3 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-sm inline-flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" /> Attiva
                </button>
                <button onClick={() => bulkAction("deactivate")} data-testid="inherit-bulk-deactivate"
                  className="h-9 px-3 rounded-sm bg-slate-500 hover:bg-slate-600 text-sm inline-flex items-center gap-1.5">
                  <EyeOff className="w-3.5 h-3.5" /> Disattiva
                </button>
                {pendingCount > 0 && (
                  <button onClick={() => bulkAction("accept-price")} data-testid="inherit-bulk-accept"
                    className="h-9 px-3 rounded-sm bg-amber-500 hover:bg-amber-600 text-sm inline-flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Accetta nuovi prezzi
                  </button>
                )}
                <button onClick={() => bulkAction("delete")} data-testid="inherit-bulk-delete"
                  className="h-9 px-3 rounded-sm bg-red-500 hover:bg-red-600 text-sm inline-flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Rimuovi
                </button>
              </>
            )}
            <button
              onClick={() => setSelectedSkus(new Set())}
              className="ml-auto h-9 w-9 rounded-sm hover:bg-white/10 inline-flex items-center justify-center"
              title="Deseleziona tutti"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Body */}
        {tab === "catalog" ? (
          <CatalogGrid
            items={catalog}
            selectedSkus={selectedSkus}
            onToggle={toggleSku}
            onToggleAll={() => toggleAll(catalog)}
          />
        ) : (
          <InheritedGrid
            items={inherited}
            selectedSkus={selectedSkus}
            onToggle={toggleSku}
            onToggleAll={() => toggleAll(inherited)}
            onUpdateMarkup={updateSingleMarkup}
            onToggleAttivo={toggleAttivo}
            onAcceptPrice={acceptPrice}
            onRemove={removeItem}
            settings={settings}
          />
        )}
      </div>

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        categorie={categorieUniche.length > 0 ? categorieUniche : Object.keys(settings?.markup_by_category || {})}
        onSaved={(s) => {
          setSettings(s);
          if (tab === "catalog") loadCatalog();
          else loadInherited();
        }}
      />
    </AdminLayout>
  );
}

// ─── Tab button ───────────────────────────────────────────────────────────────
const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 border-b-2 ${active ? "border-brand text-brand" : "border-transparent text-muted-foreground hover:text-foreground"}`}
  >
    {children}
  </button>
);

// ─── Catalog Grid ─────────────────────────────────────────────────────────────
const CatalogGrid = ({ items, selectedSkus, onToggle, onToggleAll }) => {
  if (items.length === 0) {
    return <EmptyState msg="Nessun prodotto Casa Madre nel network." />;
  }
  return (
    <>
      <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
        <button onClick={onToggleAll} className="hover:underline" data-testid="inherit-toggle-all-cat">
          Toggle tutti ({items.length})
        </button>
        <div>
          {items.filter((p) => p.inherited).length} già in vetrina
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((p) => (
          <CatalogCard key={p.sku_madre} product={p} selected={selectedSkus.has(p.sku_madre)} onToggle={() => onToggle(p.sku_madre)} />
        ))}
      </div>
    </>
  );
};

const CatalogCard = ({ product, selected, onToggle }) => (
  <div
    onClick={onToggle}
    data-testid={`cat-card-${product.sku_madre}`}
    className={`bg-white border rounded-sm cursor-pointer transition-all hover:shadow-md ${selected ? "border-brand ring-2 ring-brand/30" : "border-slate-200"}`}
  >
    <div className="aspect-square bg-slate-100 relative">
      {product.cover_url ? (
        <img src={product.cover_url} alt={product.nome} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300">
          <Package className="w-10 h-10" />
        </div>
      )}
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        data-testid={`cat-chk-${product.sku_madre}`}
        className="absolute top-2 left-2 w-5 h-5 accent-brand"
      />
      {product.inherited && (
        <span className={`absolute top-2 right-2 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${product.inherit_attivo ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
          {product.inherit_attivo ? "IN VETRINA" : "NON ATTIVA"}
        </span>
      )}
    </div>
    <div className="p-2.5">
      <div className="font-mono text-[10px] text-muted-foreground truncate">{product.sku_madre}</div>
      <div className="text-sm font-medium truncate">{product.nome}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5 capitalize">{product.categoria}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="text-xs text-muted-foreground line-through">€ {product.prezzo_base?.toFixed(2)}</span>
        <span className="text-sm font-bold text-emerald-700">€ {product.prezzo_finale_stimato?.toFixed(2)}</span>
        <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-brand/10 text-brand rounded-sm font-mono">+{product.markup_effettivo_pct}%</span>
      </div>
    </div>
  </div>
);

// ─── Inherited Grid (i miei ereditati) ────────────────────────────────────────
const InheritedGrid = ({ items, selectedSkus, onToggle, onToggleAll, onUpdateMarkup, onToggleAttivo, onAcceptPrice, onRemove, settings }) => {
  if (items.length === 0) {
    return <EmptyState msg="Non hai ancora ereditato nessun prodotto. Vai su 'Catalogo Casa Madre' per iniziare." />;
  }
  return (
    <>
      <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
        <button onClick={onToggleAll} className="hover:underline" data-testid="inherit-toggle-all-mine">
          Toggle tutti ({items.length})
        </button>
        <div>
          {items.filter((i) => i.attivo).length} attivi · {items.filter((i) => i.price_change_pending).length} con prezzo CM cambiato
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <InheritedRow
            key={it.sku_madre}
            item={it}
            selected={selectedSkus.has(it.sku_madre)}
            onToggle={() => onToggle(it.sku_madre)}
            onUpdateMarkup={onUpdateMarkup}
            onToggleAttivo={onToggleAttivo}
            onAcceptPrice={onAcceptPrice}
            onRemove={onRemove}
            settings={settings}
          />
        ))}
      </div>
    </>
  );
};

const InheritedRow = ({ item, selected, onToggle, onUpdateMarkup, onToggleAttivo, onAcceptPrice, onRemove, settings }) => {
  const [editMarkup, setEditMarkup] = useState(item.markup_pct_override ?? "");
  const isOrphan = item.orphan;

  useEffect(() => {
    setEditMarkup(item.markup_pct_override ?? "");
  }, [item.markup_pct_override]);

  const commit = () => {
    if (String(editMarkup) === String(item.markup_pct_override ?? "")) return;
    onUpdateMarkup(item.sku_madre, editMarkup);
  };

  if (isOrphan) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-sm p-3 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        <div className="flex-1 text-xs">
          <strong className="font-mono">{item.sku_madre}</strong> — il prodotto madre è stato rimosso dalla Casa Madre.
        </div>
        <button onClick={() => onRemove(item.sku_madre)} className="text-xs text-red-700 hover:underline">
          Rimuovi
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-sm p-3 flex items-center gap-3 ${selected ? "border-brand ring-1 ring-brand/30" : "border-slate-200"} ${!item.attivo ? "opacity-60" : ""}`} data-testid={`inh-row-${item.sku_madre}`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        data-testid={`inh-chk-${item.sku_madre}`}
        className="w-4 h-4 accent-brand flex-shrink-0"
      />
      {item.cover_url && (
        <img src={item.cover_url} alt="" className="w-12 h-12 rounded-sm object-cover border border-border flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] text-muted-foreground truncate">{item.sku_madre}</div>
        <div className="text-sm font-medium truncate">{item.nome}</div>
        <div className="text-[10px] text-muted-foreground capitalize">{item.categoria} · lead {item.lead_time_gg}gg</div>
      </div>

      {/* Prezzo con eventuale badge cambio */}
      <div className="text-right w-32 flex-shrink-0">
        <div className="text-xs text-muted-foreground">
          Base CM: € {item.prezzo_base_current?.toFixed(2)}
        </div>
        <div className="text-sm font-bold text-emerald-700">€ {item.prezzo_finale_current?.toFixed(2)}</div>
        {item.price_change_pending && (
          <button
            onClick={() => onAcceptPrice(item.sku_madre)}
            data-testid={`inh-accept-${item.sku_madre}`}
            className="mt-1 text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm border border-amber-300 hover:bg-amber-200 inline-flex items-center gap-1"
            title={`Snapshot: €${item.prezzo_base_snapshot?.toFixed(2)} → Nuovo: €${item.prezzo_base_current?.toFixed(2)}`}
          >
            <RefreshCw className="w-2.5 h-2.5" /> Prezzo cambiato · accetta
          </button>
        )}
      </div>

      {/* Markup */}
      <div className="w-40 flex-shrink-0">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Markup {item.markup_pct_override === null || item.markup_pct_override === undefined ? "(ereditato)" : "(override)"}
        </label>
        <div className="flex items-center gap-1 mt-0.5">
          <Input
            value={editMarkup}
            onChange={(e) => setEditMarkup(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            placeholder={`${item.markup_effettivo_pct}%`}
            data-testid={`inh-markup-${item.sku_madre}`}
            className="h-8 text-xs font-mono"
          />
          <span className="text-xs">%</span>
        </div>
        {item.markup_pct_override !== null && item.markup_pct_override !== undefined && (
          <button
            onClick={() => onUpdateMarkup(item.sku_madre, null)}
            data-testid={`inh-use-hierarchy-${item.sku_madre}`}
            className="text-[9px] text-muted-foreground hover:text-brand underline mt-0.5"
          >
            Usa gerarchia ({settings ? `${item.markup_effettivo_pct}%` : "..."})
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onToggleAttivo(item.sku_madre, item.attivo)}
          data-testid={`inh-toggle-${item.sku_madre}`}
          className={`h-8 w-8 rounded-sm inline-flex items-center justify-center border ${item.attivo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
          title={item.attivo ? "In vetrina — click per nascondere" : "Nascosto — click per pubblicare"}
        >
          {item.attivo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onRemove(item.sku_madre)}
          data-testid={`inh-remove-${item.sku_madre}`}
          className="h-8 w-8 rounded-sm inline-flex items-center justify-center hover:bg-red-50 text-red-600"
          title="Rimuovi eredità"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const EmptyState = ({ msg }) => (
  <div className="text-center py-16 text-sm text-muted-foreground italic">
    <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
    {msg}
  </div>
);

// ─── Settings Modal ───────────────────────────────────────────────────────────
const SettingsModal = ({ open, onClose, settings, categorie, onSaved }) => {
  const [markupDefault, setMarkupDefault] = useState(30);
  const [byCategory, setByCategory] = useState({});
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newPct, setNewPct] = useState("");

  useEffect(() => {
    if (settings) {
      setMarkupDefault(settings.markup_default ?? 30);
      setByCategory({ ...(settings.markup_by_category || {}) });
    }
  }, [settings, open]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/rivenditore/inherit-settings", {
        markup_default: parseFloat(markupDefault),
        markup_by_category: byCategory,
      });
      toast.success("Impostazioni salvate");
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const addCategoryMarkup = () => {
    if (!newCat || !newPct || isNaN(parseFloat(newPct))) {
      toast.warning("Categoria e % validi");
      return;
    }
    setByCategory({ ...byCategory, [newCat]: parseFloat(newPct) });
    setNewCat("");
    setNewPct("");
  };

  const removeCategoryMarkup = (cat) => {
    const next = { ...byCategory };
    delete next[cat];
    setByCategory(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="inherit-settings-modal">
      <div className="bg-white rounded-sm w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-xl">Impostazioni markup</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Priorità: override singolo → categoria → globale</p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-sm hover:bg-slate-100 inline-flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Markup globale (default)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                value={markupDefault}
                onChange={(e) => setMarkupDefault(e.target.value)}
                data-testid="settings-markup-default"
                className="h-10 rounded-sm font-mono text-lg font-bold"
                step="0.5"
                min="0"
              />
              <span className="text-lg">%</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Applicato quando non c&apos;è override per categoria o singolo prodotto.</p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Markup per categoria (opzionale)</label>
            <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
              {Object.entries(byCategory).length === 0 && (
                <div className="text-xs text-muted-foreground italic">Nessun override per categoria.</div>
              )}
              {Object.entries(byCategory).map(([cat, pct]) => (
                <div key={cat} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1">
                  <span className="capitalize text-sm flex-1">{cat}</span>
                  <Input
                    type="number"
                    value={pct}
                    onChange={(e) => setByCategory({ ...byCategory, [cat]: parseFloat(e.target.value) || 0 })}
                    data-testid={`settings-cat-${cat}`}
                    className="h-7 w-20 text-xs font-mono"
                    step="0.5"
                  />
                  <span className="text-xs">%</span>
                  <button onClick={() => removeCategoryMarkup(cat)} className="h-7 w-7 rounded-sm hover:bg-red-100 inline-flex items-center justify-center text-red-600" data-testid={`settings-remove-cat-${cat}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <select
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                data-testid="settings-new-cat"
                className="h-9 px-2 rounded-sm border border-border text-sm flex-1"
              >
                <option value="">Scegli categoria…</option>
                {(categorie || []).filter((c) => !(c in byCategory)).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <Input
                type="number"
                value={newPct}
                onChange={(e) => setNewPct(e.target.value)}
                placeholder="%"
                data-testid="settings-new-pct"
                className="h-9 w-20 text-xs font-mono"
              />
              <button onClick={addCategoryMarkup} data-testid="settings-add-cat"
                className="h-9 px-3 rounded-sm bg-foreground text-white text-xs hover:bg-brand inline-flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Aggiungi
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="h-9 px-4 rounded-sm border border-border text-sm hover:bg-white">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="settings-save"
            className="h-9 px-5 rounded-sm bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
};
